import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar, Clock, Users, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../hooks/useLanguage';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from './StripePaymentForm';
import { cn } from '@/lib/utils';
import { getDisplayTtcEuros } from '../lib/offerPrice';
import {
  getPaymentTraceSessionId,
  paymentTrace,
  paymentTraceHeaders,
} from '../lib/paymentTrace';

/** WowBooking exposes min_participants / max_capacity on each offer; UI must respect both. */
function getParticipantBounds(offer) {
  if (!offer) return { min: 1, max: 10 };
  const rawMin = Number(offer.min_participants);
  const min = Number.isFinite(rawMin) && rawMin >= 1 ? rawMin : 1;
  const rawMax = Number(offer.max_capacity);
  const max =
    Number.isFinite(rawMax) && rawMax >= min ? rawMax : Math.max(min, 10);
  return { min, max };
}

function clampParticipants(count, offer) {
  const { min, max } = getParticipantBounds(offer);
  return Math.min(max, Math.max(min, count));
}

/** WowBooking: trust is_bookable when present; legacy APIs may only expose capacity_available. */
function isSlotBookable(slot) {
  if (!slot) return false;
  if (typeof slot.is_bookable === 'boolean') {
    return slot.is_bookable === true;
  }
  return slot.capacity_available > 0;
}

function mapCouponReasonToI18nKey(reason) {
  const normalized = String(reason || '').trim().toLowerCase();
  if (!normalized) return 'booking.couponInvalid';
  const byReason = {
    expired: 'booking.couponReasonExpired',
    offer_not_allowed: 'booking.couponReasonOfferNotAllowed',
    quota_exhausted: 'booking.couponReasonQuotaExhausted',
    customer_email_required: 'booking.couponReasonCustomerEmailRequired',
    min_order_amount_not_reached: 'booking.couponReasonMinOrder',
    min_order_not_reached: 'booking.couponReasonMinOrder',
    not_started: 'booking.couponReasonNotStarted',
    booking_period_not_allowed: 'booking.couponReasonBookingPeriod',
    booking_date_not_allowed: 'booking.couponReasonBookingPeriod',
  };
  return byReason[normalized] || 'booking.couponInvalid';
}

const BookingModal = ({
  isOpen,
  onClose,
  offer,
  embedded = false,
  suppressBookingHeader = false,
  className,
  onFirstInteraction,
}) => {
  const { language, t } = useLanguage();
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [participants, setParticipants] = useState(2);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [dateAvailability, setDateAvailability] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [calendarPage, setCalendarPage] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const firstInteractRef = useRef(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  /** Offer page layout: widen columns only after a meaningful click (embedded + callback). */
  const notifyColumnExpandIntent = useCallback(() => {
    if (!embedded || !onFirstInteraction) return;
    if (firstInteractRef.current) return;
    firstInteractRef.current = true;
    onFirstInteraction();
  }, [embedded, onFirstInteraction]);

  useEffect(() => {
    if (!(isOpen || embedded)) return;
    loadStripeConfig();
  }, [isOpen, embedded]);

  useEffect(() => {
    if (!offer) return;
    setParticipants((p) => clampParticipants(p, offer));
  }, [offer?.id, offer?.min_participants, offer?.max_capacity]);

  useEffect(() => {
    if ((isOpen || embedded) && offer) {
      loadDateAvailability();
    }
  }, [isOpen, embedded, offer, participants, calendarPage]);

  useEffect(() => {
    if (selectedDate && offer) {
      loadAvailableSlots();
    }
  }, [selectedDate, participants]);

  useEffect(() => {
    if (selectedSlot && offer) {
      calculatePricing();
    }
  }, [selectedSlot, participants]);

  const parseAmount = useCallback((value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const estimatedUnitAmount = getDisplayTtcEuros(offer);
  const estimatedMode = String(offer?.pricing_mode || '').toLowerCase();
  const estimatedTotalAmount =
    estimatedMode === 'per_person'
      ? estimatedUnitAmount * Math.max(1, Number(participants) || 1)
      : estimatedUnitAmount;
  const baseTotalAmount = parseAmount(pricing?.total ?? pricing?.amount ?? estimatedTotalAmount);
  const normalizedCouponCode = couponCode.trim().toUpperCase();
  const hasCouponCode = normalizedCouponCode.length > 0;
  const couponMatchesCurrentInput =
    !!couponValidation && couponValidation.code === normalizedCouponCode;
  const isCouponEligible =
    couponMatchesCurrentInput && Boolean(couponValidation?.eligible);
  const discountAmount = isCouponEligible ? parseAmount(couponValidation?.discount_ttc) : 0;
  const finalTotalAmount = isCouponEligible
    ? parseAmount(couponValidation?.final_ttc)
    : baseTotalAmount;
  const couponNeedsValidation = hasCouponCode && (!couponMatchesCurrentInput || isValidatingCoupon);
  const couponIsInvalid = hasCouponCode && !couponNeedsValidation && !isCouponEligible;
  const couponInvalidMessage = (() => {
    if (!couponMatchesCurrentInput || isCouponEligible) return t('booking.couponInvalid');
    const key = mapCouponReasonToI18nKey(couponValidation?.reason);
    const translated = t(key);
    if (translated && translated !== key) return translated;
    const fallbackReason = String(couponValidation?.reason || '').trim();
    return fallbackReason
      ? fallbackReason.replaceAll('_', ' ')
      : t('booking.couponInvalid');
  })();

  useEffect(() => {
    if (!hasCouponCode) {
      setCouponValidation(null);
      setIsValidatingCoupon(false);
      return;
    }
    if (!offer?.id || baseTotalAmount <= 0 || !selectedSlot?.id) {
      setCouponValidation(null);
      setIsValidatingCoupon(false);
      return;
    }

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setIsValidatingCoupon(true);
      try {
        const response = await axios.post(`${API_URL}/api/bookings/validate-coupon`, {
          offer_id: offer.id,
          code: normalizedCouponCode,
          base_amount_ttc: baseTotalAmount.toFixed(2),
          slot_id: selectedSlot.id,
          booking_date: selectedSlot.date,
          booking_start_time: selectedSlot.start_time?.substring(0, 5),
          currency: 'EUR',
          customer_email: customerInfo.email || undefined
        });
        if (cancelled) return;
        setCouponValidation({
          ...response.data,
          code: normalizedCouponCode
        });
      } catch (error) {
        if (cancelled) return;
        if (error?.response?.status === 429) {
          toast.error('Too many coupon attempts. Please wait a moment and retry.');
        }
        const detail = error?.response?.data?.detail;
        const reasonFromDetail =
          typeof detail === 'string'
            ? detail
            : (detail && typeof detail === 'object' ? detail.reason || detail.detail : null);
        setCouponValidation({
          eligible: false,
          discount_ttc: '0.00',
          final_ttc: baseTotalAmount.toFixed(2),
          code: normalizedCouponCode,
          reason: reasonFromDetail || null
        });
      } finally {
        if (!cancelled) setIsValidatingCoupon(false);
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    API_URL,
    baseTotalAmount,
    customerInfo.email,
    hasCouponCode,
    normalizedCouponCode,
    offer?.id,
    selectedSlot?.id,
    selectedSlot?.date,
    selectedSlot?.start_time
  ]);

  const loadDateAvailability = async () => {
    setLoadingAvailability(true);
    try {
      const today = new Date();
      const pageStart = new Date(today);
      pageStart.setDate(today.getDate() + calendarPage * 28);
      const pageEnd = new Date(pageStart);
      pageEnd.setDate(pageStart.getDate() + 27);

      const formatDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${dd}`;
      };

      const startDate = formatDate(pageStart);
      const endDate = formatDate(pageEnd);

      const response = await axios.get(
        `${API_URL}/api/wowbooking/offers/${offer.id}/availability`,
        { params: { start_date: startDate, end_date: endDate, participants } }
      );

      const allSlots = response.data || [];
      const availMap = { ...dateAvailability };

      // Initialize page dates as unavailable
      for (let i = 0; i < 28; i++) {
        const d = new Date(pageStart);
        d.setDate(pageStart.getDate() + i);
        availMap[formatDate(d)] = false;
      }

      // Mark dates that have at least one bookable slot
      allSlots.forEach(slot => {
        if (isSlotBookable(slot)) {
          availMap[slot.date] = true;
        }
      });

      setDateAvailability(availMap);
    } catch (error) {
      console.error('Failed to load date availability:', error);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const loadAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      // Use local date to avoid timezone shift (toISOString converts to UTC which can shift the day)
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const startDate = `${year}-${month}-${day}`;
      const endDate = startDate;
      
      const response = await axios.get(
        `${API_URL}/api/wowbooking/offers/${offer.id}/availability`,
        { params: { start_date: startDate, end_date: endDate, participants } }
      );
      
      const allSlots = response.data || [];

      // Filter slots for the selected date only
      const slotsForDate = allSlots.filter(slot => slot.date === startDate);

      // Group by start_time to avoid duplicates (bookable slots only)
      const uniqueSlots = {};
      slotsForDate.filter(isSlotBookable).forEach(slot => {
        const timeKey = slot.start_time;
        if (!uniqueSlots[timeKey] || slot.capacity_available > uniqueSlots[timeKey].capacity_available) {
          uniqueSlots[timeKey] = slot;
        }
      });

      // Convert back to array and sort by time
      const filteredSlots = Object.values(uniqueSlots)
        .filter(isSlotBookable)
        .sort((a, b) => a.start_time.localeCompare(b.start_time));

      setAvailableSlots(filteredSlots);
    } catch (error) {
      console.error('Failed to load slots:', error);
      toast.error('Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const calculatePricing = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/wowbooking/pricing/calculate`, {
        offer_id: offer.id,
        slot_id: selectedSlot.id,
        participants
      });
      setPricing(response.data);
    } catch (error) {
      console.error('Failed to calculate pricing:', error);
      const unitTtc = getDisplayTtcEuros(offer);
      if (unitTtc > 0) {
        const mode = String(offer.pricing_mode || 'per_person');
        const totalTtc =
          mode === 'per_unit' ? unitTtc : unitTtc * Math.max(1, Number(participants) || 1);
        setPricing({ total: totalTtc.toFixed(2) });
      }
    }
  };

  const loadStripeConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stripe/config`);
      const stripe = await loadStripe(response.data.publishable_key);
      setStripePromise(stripe);
    } catch (error) {
      console.error('Failed to load Stripe config:', error);
      if (isOpen || embedded) {
        toast.error('Payment system unavailable. Please try again later.');
      }
    }
  };

  const handleBooking = async () => {
    if (couponNeedsValidation || couponIsInvalid) {
      toast.error(couponInvalidMessage);
      return;
    }

    setIsSubmitting(true);
    paymentTrace('booking.create.start', { offer_id: offer?.id, participants });
    try {
      // Create booking in pending status
      const response = await axios.post(
        `${API_URL}/api/bookings`,
        {
          offer_id: offer.id,
          slot_id: selectedSlot.id,
          participants,
          customer: customerInfo,
          booking_date: selectedSlot.date,
          amount: finalTotalAmount,
          amount_before_discount: baseTotalAmount,
          discount_amount: discountAmount,
          coupon_code: isCouponEligible ? normalizedCouponCode : null,
          session_id: getPaymentTraceSessionId(),
        },
        { headers: paymentTraceHeaders() }
      );
      
      const newBookingId = response.data.booking_id;
      setBookingId(newBookingId);
      paymentTrace('booking.create.ok', { booking_id: newBookingId });
      
      // Create payment intent
      const paymentResponse = await axios.post(
        `${API_URL}/api/stripe/create-payment-intent`,
        {
          booking_id: newBookingId,
          amount: finalTotalAmount,
          session_id: getPaymentTraceSessionId(),
        },
        { headers: paymentTraceHeaders() }
      );
      
      setClientSecret(paymentResponse.data.client_secret);
      paymentTrace('payment_intent.created', {
        booking_id: newBookingId,
        payment_intent_id: paymentResponse.data.payment_intent_id,
      });
      setStep(4); // Move to payment step
      
    } catch (error) {
      paymentTrace('booking.create.error', { message: error?.response?.data?.detail || error?.message });
      console.error('Failed to create booking:', error);
      if (error?.response?.data?.detail === 'invalid_or_unavailable_coupon') {
        toast.error(t('booking.couponInvalid'));
        return;
      }
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    paymentTrace('payment.success.redirecting', {
      booking_id: bookingId,
      payment_intent_id: paymentIntent?.id,
      status: paymentIntent?.status,
    });

    // Start WowBooking sync in background; confirmation page handles the wait + retry.
    axios
      .post(
        `${API_URL}/api/bookings/${bookingId}/confirm-wowbooking`,
        {},
        { headers: paymentTraceHeaders() }
      )
      .then(() => paymentTrace('confirm_wowbooking.ok', { booking_id: bookingId }))
      .catch((error) =>
        paymentTrace('confirm_wowbooking.error', {
          booking_id: bookingId,
          status: error?.response?.status,
          detail: error?.response?.data?.detail,
        })
      );

    onClose();
    window.location.assign(`/booking-confirmation?booking_id=${encodeURIComponent(bookingId)}`);
  };

  const handlePaymentError = (error) => {
    paymentTrace('payment.error', {
      booking_id: bookingId,
      message: error?.message,
      code: error?.code,
    });
    console.error('Payment error:', error);
    toast.error('Payment failed. Please try again.');
  };

  const generateCalendarDays = () => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() + calendarPage * 28);
    const days = [];
    for (let i = 0; i < 28; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const calendarDays = generateCalendarDays();

  if (!offer) return null;

  const { min: minParticipants, max: maxParticipants } = getParticipantBounds(offer);
  const pricingMode = String(offer.pricing_mode || '').toLowerCase();
  const isPerPersonPricing = pricingMode === 'per_person';
  const unitTtc = getDisplayTtcEuros(offer);
  const computedTotalTtc = isPerPersonPricing
    ? unitTtc * Math.max(1, Number(participants) || 1)
    : unitTtc;

  const headerBlock =
    !suppressBookingHeader ? (
      <DialogHeader>
        <DialogTitle className="text-2xl font-bold text-primary-950">{t('booking.title')}</DialogTitle>
        <DialogDescription>{t('booking.subtitle')}</DialogDescription>
      </DialogHeader>
    ) : null;

  const flowInner = (
    <>
      {headerBlock}

      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= s ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-1 ${step > s ? 'bg-primary-600' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Date & Participants */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <Label className="text-lg font-semibold mb-3 block">{t('booking.selectParticipants')}</Label>
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    notifyColumnExpandIntent();
                    setParticipants(Math.max(minParticipants, participants - 1));
                  }}
                  disabled={participants <= minParticipants}
                >
                  -
                </Button>
                <div className="flex items-center gap-2 px-4">
                  <Users className="w-5 h-5 text-primary-600" />
                  <span className="text-2xl font-bold">{participants}</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    notifyColumnExpandIntent();
                    setParticipants(Math.min(maxParticipants, participants + 1));
                  }}
                  disabled={participants >= maxParticipants}
                >
                  +
                </Button>
              </div>
              {unitTtc > 0 && (
                <div className="mt-4 rounded-lg border border-primary-100 bg-primary-50 p-4">
                  <div className="text-sm text-gray-600">{t('booking.total')}</div>
                  <div className="text-2xl font-bold text-primary-700">€{computedTotalTtc}</div>
                  <div className="text-xs text-gray-500">
                    €{unitTtc} / {isPerPersonPricing ? t('offerCard.perPerson') : t('offerCard.perGroup')}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-lg font-semibold block">{t('booking.selectDate')}</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCalendarPage(Math.max(0, calendarPage - 1))}
                    disabled={calendarPage === 0 || loadingAvailability}
                    className="px-2 py-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600 min-w-[100px] text-center">
                    {calendarDays[0]?.toLocaleDateString(language, { month: 'short', day: 'numeric' })}
                    {' - '}
                    {calendarDays[calendarDays.length - 1]?.toLocaleDateString(language, { month: 'short', day: 'numeric' })}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCalendarPage(calendarPage + 1)}
                    disabled={loadingAvailability}
                    className="px-2 py-1"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {loadingAvailability && calendarPage > 0 && (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                </div>
              )}
              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((date, idx) => {
                  const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                  const isToday = date.toDateString() === new Date().toDateString();
                  
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        notifyColumnExpandIntent();
                        setSelectedDate(date);
                        setSelectedSlot(null);
                      }}
                      className={`p-2 rounded-lg text-center transition-all relative ${
                        isSelected
                          ? 'bg-primary-600 text-white'
                          : isToday
                          ? 'bg-primary-50 text-primary-900 border-2 border-primary-600'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                      }`}
                    >
                      <div className="text-xs font-medium">
                        {date.toLocaleDateString(language, { weekday: 'short' })}
                      </div>
                      <div className="text-lg font-bold">{date.getDate()}</div>
                      <div className="text-xs">
                        {date.toLocaleDateString(language, { month: 'short' })}
                      </div>
                      {(() => {
                        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        const hasAvailability = dateAvailability[dateKey];
                        if (hasAvailability === undefined) return null;
                        return (
                          <span className={`absolute top-1 right-1 w-2 h-2 rounded-full ${hasAvailability ? 'bg-green-500' : 'bg-red-500'}`} />
                        );
                      })()}
                    </button>
                  );
                })}
              </div>
            </div>

            <Button
              type="button"
              onClick={() => setStep(2)}
              disabled={!selectedDate}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white"
            >
              {t('booking.continueToTime')}
            </Button>
          </div>
        )}

        {/* Step 2: Time Slot Selection */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('booking.selectedDate')}</p>
                <p className="font-semibold">{selectedDate?.toLocaleDateString(language, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-1" /> {t('booking.change')}
              </Button>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-3 block">{t('booking.availableSlots')}</Label>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {t('booking.noSlots')}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {availableSlots.map((slot) => {
                    const isLowCapacity = slot.capacity_available <= 2 && slot.capacity_available > 0;
                    const isSelected = selectedSlot?.id === slot.id;
                    
                    return (
                      <button
                        key={slot.id}
                        type="button"
                        onClick={() => setSelectedSlot(slot)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-primary-600 bg-primary-50'
                            : isLowCapacity
                            ? 'border-orange-300 bg-orange-50 hover:border-orange-400'
                            : 'border-gray-200 hover:border-primary-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 justify-center">
                          <Clock className="w-4 h-4" />
                          <span className="font-semibold">{slot.start_time.substring(0, 5)}</span>
                        </div>
                        <div className={`text-xs mt-1 ${isLowCapacity ? 'text-orange-600 font-semibold' : 'text-gray-500'}`}>
                          {slot.capacity_available} {slot.capacity_available > 1 ? t('booking.spotsLeftPlural') : t('booking.spotsLeft')}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> {t('booking.back')}
              </Button>
              <Button
                type="button"
                onClick={() => setStep(3)}
                disabled={!selectedSlot}
                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white"
              >
                {t('booking.continueToDetails')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Customer Information */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="bg-primary-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">{t('booking.summary')}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => setStep(1)}>
                  {t('booking.edit')}
                </Button>
              </div>
              <div className="text-sm space-y-1">
                <p><strong>{t('booking.date')}:</strong> {selectedDate?.toLocaleDateString(language)}</p>
                <p><strong>{t('booking.time')}:</strong> {selectedSlot?.start_time}</p>
                <p><strong>{t('booking.participants')}:</strong> {participants}</p>
                {pricing && (
                  <>
                    {isCouponEligible && (
                      <p className="text-sm text-gray-600">
                        <strong>{t('booking.total')}:</strong> €{baseTotalAmount.toFixed(2)}
                      </p>
                    )}
                    {isCouponEligible && (
                      <p className="text-sm text-green-700">
                        <strong>{t('booking.discount')}:</strong> -€{discountAmount.toFixed(2)}
                      </p>
                    )}
                    <p className="text-lg font-bold text-primary-600 mt-2">
                      {t('booking.total')}: €{finalTotalAmount.toFixed(2)}
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="couponCode">{t('booking.couponCode')}</Label>
              <Input
                id="couponCode"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                placeholder={t('booking.couponPlaceholder')}
                maxLength={64}
              />
              {isValidatingCoupon && (
                <p className="text-xs text-gray-500">{t('booking.couponValidating')}</p>
              )}
              {!isValidatingCoupon && hasCouponCode && couponMatchesCurrentInput && isCouponEligible && (
                <p className="text-xs text-green-700">
                  {t('booking.couponApplied')} -€{discountAmount.toFixed(2)}
                </p>
              )}
              {!isValidatingCoupon && hasCouponCode && couponMatchesCurrentInput && !isCouponEligible && (
                <p className="text-xs text-red-600">{couponInvalidMessage}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t('booking.firstName')}</Label>
                <Input
                  id="firstName"
                  value={customerInfo.firstName}
                  onChange={(e) => setCustomerInfo({...customerInfo, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t('booking.lastName')}</Label>
                <Input
                  id="lastName"
                  value={customerInfo.lastName}
                  onChange={(e) => setCustomerInfo({...customerInfo, lastName: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('booking.email')}</Label>
              <Input
                id="email"
                type="email"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('booking.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                required
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(2)}
                className="flex-1"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> {t('booking.back')}
              </Button>
              <Button
                type="button"
                onClick={handleBooking}
                disabled={
                  isSubmitting ||
                  !customerInfo.firstName ||
                  !customerInfo.lastName ||
                  !customerInfo.email ||
                  !customerInfo.phone ||
                  couponNeedsValidation ||
                  couponIsInvalid
                }
                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('booking.processing')}
                  </>
                ) : (
                  t('booking.continueToPayment')
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Payment */}
        {step === 4 && clientSecret && stripePromise && (
          <div className="space-y-6">
            <div className="bg-primary-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">{t('booking.bookingSummary')}</span>
              </div>
              <div className="text-sm space-y-1">
                <p><strong>{t('booking.date')}:</strong> {selectedDate?.toLocaleDateString(language)}</p>
                <p><strong>{t('booking.time')}:</strong> {selectedSlot?.start_time}</p>
                <p><strong>{t('booking.participants')}:</strong> {participants}</p>
                <p><strong>{t('booking.name')}:</strong> {customerInfo.firstName} {customerInfo.lastName}</p>
                <p><strong>{t('booking.email')}:</strong> {customerInfo.email}</p>
                {isCouponEligible && (
                  <p><strong>{t('booking.discount')}:</strong> -€{discountAmount.toFixed(2)}</p>
                )}
                <p className="font-semibold"><strong>{t('booking.total')}:</strong> €{finalTotalAmount.toFixed(2)}</p>
              </div>
            </div>

            {/* key forces a full Elements remount when clientSecret changes (required by Stripe). */}
            <Elements
              key={clientSecret}
              stripe={stripePromise}
              options={{ clientSecret }}
            >
              <StripePaymentForm
                amount={finalTotalAmount}
                bookingId={bookingId}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </Elements>

            <div className="text-center">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  // Drop the unused PaymentIntent UI so the next attempt remounts cleanly.
                  setClientSecret(null);
                  setStep(3);
                }}
                className="text-sm text-gray-600"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> {t('booking.backToDetails')}
              </Button>
            </div>

            <div className="text-xs text-gray-500 text-center">
              {t('booking.expiryWarning')}
            </div>
          </div>
        )}
    </>
  );

  // Stripe Payment Element iframes break inside overflow/max-height ancestors on mobile
  // Chrome (blank payment step → abandoned PaymentIntents). Keep scroll only on steps 1–3.
  const constrainScroll = step !== 4;

  if (embedded) {
    return (
      <div
        id="offer-booking-flow"
        className={cn(
          'w-full overflow-x-hidden',
          constrainScroll && 'max-h-[min(85vh,920px)] overflow-y-auto',
          className
        )}
      >
        {flowInner}
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'sm:max-w-[500px] bg-white',
          constrainScroll && 'max-h-[90vh] overflow-y-auto'
        )}
      >
        {flowInner}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
